import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useFeaturePermissions } from '../../../components/globalfeatures';
import { ticketService } from '../../../api/ticketService';
import { extractErrorMessage } from '../../../utils/errorHandler';
import {
  parseBulkTicketImportFile,
  downloadBulkTicketImportTemplate,
  BULK_IMPORT_ROW_STATUS,
} from '../../../utils/bulkTicketImport';

const BulkTicketImport = () => {
  const bulkImportPerms = useFeaturePermissions('bulk_ticket_import');
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [result, setResult] = useState(null);

  if (!bulkImportPerms.canView) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6 flex items-center gap-3 text-amber-800">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <p>You do not have permission to access bulk ticket import.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      await downloadBulkTicketImportTemplate();
      toast.success('Template downloaded (see reference sheets for IDs)');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to build template'));
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.csv') && !lower.endsWith('.xlsx') && !lower.endsWith('.xls')) {
      toast.error('Please upload CSV or Excel (.xlsx, .xls)');
      return;
    }

    setSelectedFile(file);
    setResult(null);
    setParsing(true);
    try {
      const rows = await parseBulkTicketImportFile(file);
      if (rows.length === 0) {
        toast.error('No valid rows found. Check required columns and data.');
        setParsedRows([]);
        setSelectedFile(null);
        return;
      }
      setParsedRows(rows);
      toast.success(`Parsed ${rows.length} row(s) ready to import`);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to parse file'));
      setParsedRows([]);
      setSelectedFile(null);
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (parsedRows.length === 0) {
      toast.error('Upload a file with at least one valid row first');
      return;
    }

    setImporting(true);
    setResult(null);
    try {
      const response = await ticketService.bulkImportTickets({
        rows: parsedRows,
        originalFileName: selectedFile?.name || 'import.json',
      });
      const payload = response?.data?.data;
      if (!payload) {
        throw new Error(response?.data?.message || 'Import failed');
      }
      setResult(payload);
      const { importedCount, validationRejectedCount, duplicateRejectedCount } = payload;
      toast.success(
        `Import finished: ${importedCount} imported, ${validationRejectedCount} validation rejected, ${duplicateRejectedCount} duplicate rejected`,
      );
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Bulk import failed'));
    } finally {
      setImporting(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setParsedRows([]);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderStatusBadge = (status) => {
    if (status === BULK_IMPORT_ROW_STATUS.IMPORTED) {
      return <Badge className="bg-green-100 text-green-800">IMPORTED</Badge>;
    }
    if (status === BULK_IMPORT_ROW_STATUS.DUPLICATE_REJECTED) {
      return <Badge className="bg-amber-100 text-amber-900">DUPLICATE_REJECTED</Badge>;
    }
    return <Badge variant="destructive">VALIDATION_REJECTED</Badge>;
  };

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-5xl mx-auto">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Bulk Ticket Import</h1>
        <p className="text-sm text-gray-600 mt-1">
          Import Inquiry tickets from Excel or CSV. Imported tickets are unassigned and appear in the HDO pending queue.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Template &amp; upload
          </CardTitle>
          <CardDescription>
            Use the same fields as Log Customer Ticket (plus requester email per row). Kenya is applied automatically — do not enter countryId. See reference sheets for inquiry and location IDs. Attachments are not imported.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
            >
              <Download className="h-4 w-4 mr-2" />
              {downloadingTemplate ? 'Building template…' : 'Download Excel template'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              {parsing ? 'Parsing…' : 'Choose CSV / Excel file'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {selectedFile && (
            <p className="text-sm text-gray-700">
              File: <span className="font-medium">{selectedFile.name}</span>
              {parsedRows.length > 0 && (
                <span className="ml-2 text-gray-500">({parsedRows.length} row(s) ready)</span>
              )}
            </p>
          )}

          <div className="flex gap-3">
            <Button
              type="button"
              onClick={handleImport}
              disabled={importing || parsedRows.length === 0}
            >
              {importing ? 'Importing…' : 'Run import'}
            </Button>
            {(parsedRows.length > 0 || result) && (
              <Button type="button" variant="ghost" onClick={resetForm}>
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Import summary
            </CardTitle>
            <CardDescription>
              Batch reference: <span className="font-mono font-medium">{result.batchReference}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="rounded-md border p-3">
                <div className="text-gray-500">Total rows</div>
                <div className="text-lg font-semibold">{result.totalRows}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-gray-500">Imported</div>
                <div className="text-lg font-semibold text-green-700">{result.importedCount}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-gray-500">Validation rejected</div>
                <div className="text-lg font-semibold text-red-700">{result.validationRejectedCount}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-gray-500">Duplicate rejected</div>
                <div className="text-lg font-semibold text-amber-700">{result.duplicateRejectedCount}</div>
              </div>
            </div>

            {Array.isArray(result.rowOutcomes) && result.rowOutcomes.length > 0 && (
              <div className="overflow-x-auto border rounded-md">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2 font-medium">Row</th>
                      <th className="text-left p-2 font-medium">Status</th>
                      <th className="text-left p-2 font-medium">Ticket</th>
                      <th className="text-left p-2 font-medium">Duplicate of</th>
                      <th className="text-left p-2 font-medium">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rowOutcomes.map((row) => (
                      <tr key={row.rowNumber} className="border-t">
                        <td className="p-2">{row.rowNumber}</td>
                        <td className="p-2">{renderStatusBadge(row.status)}</td>
                        <td className="p-2 font-mono">{row.ticketNumber || '—'}</td>
                        <td className="p-2 font-mono">{row.duplicateTicketNumber || '—'}</td>
                        <td className="p-2 text-gray-700">{row.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkTicketImport;
